/**
 * Pair Trade Executor
 * Executes pair trades using chase limit orders for both legs
 *
 * Uses ChaseUtils for shared functionality (consolidated patterns from multiple executors)
 */

class PairTradeExecutor {
    constructor() {
        this.activeExecutions = new Map();
        this.CHASE_TIMEOUT_MS = 60000; // 60 seconds max chase time
        this.CHASE_SETTINGS = {
            tickDistance: 5,       // 5 ticks from mid
            frequencyRangeMin: 5,  // Check every 5 seconds
            frequencyRangeMax: 5,  // Fixed 5 second intervals
            useAnchor: false,
            tpslEnabled: false,
            aggressive: true       // Cross spread: +5 above mid for long, -5 below mid for short
        };
        this.INITIAL_PERCENT_OFFSET = 0;  // Start at market price
        // console.log('[PAIR-EXECUTOR] ✅ Initialized');
    }

    // Helper to get ChaseUtils (may not be loaded immediately)
    get utils() {
        return window.ChaseUtils;
    }

    /**
     * Execute a pair trade with chase limit orders
     * @param {Object} params - Trade parameters
     * @param {string} params.symbolA - First symbol (e.g., "ETH")
     * @param {string} params.symbolB - Second symbol (e.g., "BTC")
     * @param {string} params.directionA - Direction for leg A ("LONG" or "SHORT")
     * @param {number} params.hedgeRatio - Hedge ratio from Kalman filter
     * @param {string} params.riskInput - Risk input from user (e.g., "2%", "$200")
     * @param {number} params.walletBalance - Current wallet balance
     * @returns {Promise<Object>} Execution result
     */
    async executePairTrade(params) {
        const {
            symbolA, symbolB, directionA, hedgeRatio,
            riskInput, walletBalance,
            // Auto SL/TP parameters (optional)
            autoSlTp = false,
            atrA = 0,
            atrB = 0,
            priceA: inputPriceA = 0,
            priceB: inputPriceB = 0,
            timeframe = '1h' // '1h' = 1.5x ATR, '1m' = 0.5x ATR for SL/TP
        } = params;

        const executionId = `pair_${Date.now()}`;
        const directionB = directionA === 'LONG' ? 'SHORT' : 'LONG';

        // console.log(`[PAIR-EXECUTOR] Starting ${executionId}: ${directionA} ${symbolA} / ${directionB} ${symbolB}`);

        try {
            // Calculate sizes using risk utils
            const { parseRiskInput, calculateLegSizes, usdToAssetSize } = window.PairsRiskUtils || {};
            if (!parseRiskInput) {
                throw new Error('PairsRiskUtils not loaded');
            }

            const totalRisk = parseRiskInput(riskInput, walletBalance);
            const { legA, legB } = calculateLegSizes(totalRisk, hedgeRatio);

            // console.log(`[PAIR-EXECUTOR] Risk: $${totalRisk.toFixed(2)}, Leg A: $${legA.toFixed(2)}, Leg B: $${legB.toFixed(2)}`);

            // Get current prices
            const priceA = await this.getPrice(symbolA);
            const priceB = await this.getPrice(symbolB);

            if (!priceA || !priceB) {
                throw new Error('Failed to get prices');
            }

            const sizeA = usdToAssetSize(legA, priceA);
            const sizeB = usdToAssetSize(legB, priceB);

            // Pre-order size validation (prevents zero/NaN from reaching placeLegOrder)
            if (!sizeA || sizeA <= 0 || !Number.isFinite(sizeA)) {
                throw new Error(`Invalid size for ${symbolA}: ${sizeA} (legA=$${legA}, price=${priceA})`);
            }
            if (!sizeB || sizeB <= 0 || !Number.isFinite(sizeB)) {
                throw new Error(`Invalid size for ${symbolB}: ${sizeB} (legB=$${legB}, price=${priceB})`);
            }

            // Store execution state
            this.activeExecutions.set(executionId, {
                symbolA, symbolB, directionA, directionB,
                sizeA, sizeB, legA, legB,
                priceA, priceB,
                startTime: Date.now(),
                status: 'EXECUTING',
                currentOidA: null,
                currentOidB: null,
                legAFilled: 0,
                legBFilled: 0
            });

            // Log sizes before placing (helps debug size rounding issues)
            console.log(`[PAIR-EXECUTOR] Calculated sizes: ${symbolA}=${sizeA}, ${symbolB}=${sizeB} (from $${legA.toFixed(2)} + $${legB.toFixed(2)})`);

            // Place legs sequentially to avoid race condition on shared assetSzDecimals
            // (BrowserHyperliquidClient uses shared instance state that gets overwritten)
            const resultA = await this.placeLegOrder(symbolA, directionA, sizeA, priceA);
            const resultB = await this.placeLegOrder(symbolB, directionB, sizeB, priceB);

            // Check for failures with detailed error messages
            if (!resultA.success || !resultB.success) {
                const errors = [];
                if (!resultA.success) errors.push(`${symbolA}: ${resultA.error || 'unknown'}`);
                if (!resultB.success) errors.push(`${symbolB}: ${resultB.error || 'unknown'}`);

                // Cancel successful orders with retry logic (prevents orphaned single-leg positions)
                if (resultA.success && resultA.orderId) {
                    await this.cancelOrderWithRetry(symbolA, resultA.orderId);  // Uses default 4 attempts for 1s/2s/4s backoff
                }
                if (resultB.success && resultB.orderId) {
                    await this.cancelOrderWithRetry(symbolB, resultB.orderId);  // Uses default 4 attempts for 1s/2s/4s backoff
                }
                throw new Error(`Failed to place legs: ${errors.join(', ')}`);
            }

            // Store order IDs
            const execution = this.activeExecutions.get(executionId);
            execution.currentOidA = resultA.orderId;
            execution.currentOidB = resultB.orderId;

            // Start chase immediately for both legs
            // Delta monitoring in ChaseTracker handles fill detection (500ms polling)
            // No need for waitForOrderConfirmation or instant-fill checks anymore
            console.log(`[PAIR-EXECUTOR] Starting chase for ${symbolA}:`, { oid: resultA.orderId, price: resultA.limitPrice });
            await this.startChase(resultA.orderId, symbolA, directionA, sizeA, resultA.limitPrice);
            if (window.showNotification) {
                window.showNotification(`Chase started: ${directionA} ${symbolA} @ $${resultA.limitPrice.toFixed(2)}`, 'info');
            }

            console.log(`[PAIR-EXECUTOR] Starting chase for ${symbolB}:`, { oid: resultB.orderId, price: resultB.limitPrice });
            await this.startChase(resultB.orderId, symbolB, directionB, sizeB, resultB.limitPrice);
            if (window.showNotification) {
                window.showNotification(`Chase started: ${directionB} ${symbolB} @ $${resultB.limitPrice.toFixed(2)}`, 'info');
            }

            // Monitor until both filled or timeout
            const result = await this.monitorExecution(executionId);

            // Place auto SL/TP orders after both legs are filled
            // Entry prices are fetched from positions inside placeSkewedSlTp (like chase-tracker)
            if (result.status === 'COMPLETED' && autoSlTp && atrA > 0 && atrB > 0) {
                const execution = this.activeExecutions.get(executionId) || result;
                await this.placeSkewedSlTp({
                    symbolA, symbolB,
                    directionA, directionB,
                    sizeA: execution.sizeA || sizeA,
                    sizeB: execution.sizeB || sizeB,
                    atrA, atrB,
                    timeframe
                });
            }

            // Register with auto-close bot if available
            if (result.status === 'COMPLETED' && window.PairsAutoCloseBot) {
                window.PairsAutoCloseBot.registerPosition({
                    executionId,
                    symbolA, symbolB,
                    directionA, directionB,
                    sizeA, sizeB,
                    entryPriceA: priceA,
                    entryPriceB: priceB,
                    entryZScore: params.zscore || 0,
                    halfLife: params.halfLife || 60  // Pass half-life for time-based exit
                });
            }

            return result;

        } catch (error) {
            console.error(`[PAIR-EXECUTOR] Error:`, error);
            this.activeExecutions.delete(executionId);
            throw error;
        }
    }

    async getPrice(symbol) {
        // Delegate to ChaseUtils for consistent price fetching (handles HIP-3)
        if (!this.utils) {
            console.error('[PAIR-EXECUTOR] ChaseUtils not loaded');
            return null;
        }
        return this.utils.getPrice(symbol);
    }

    async placeLegOrder(symbol, direction, size, price) {
        // Delegate to ChaseUtils for consistent order placement
        const result = await this.utils?.placeLimitOrder(symbol, direction, size, price, this.INITIAL_PERCENT_OFFSET);
        if (result?.success) {
            console.log(`[PAIR-EXECUTOR] ✅ ${symbol} order placed via ChaseUtils: oid=${result.orderId}`);
        }
        return result || { success: false, error: 'ChaseUtils not available' };
    }

    /**
     * Calculate tick size from price decimals
     * Uses same logic as chase-tracker.js calculateTickSizeFromOrderbook
     */
    getTickSizeFromPrice(price) {
        const priceStr = String(price);
        const decimals = priceStr.includes('.') ? priceStr.split('.')[1].length : 0;
        const tickSize = Math.pow(10, -decimals);
        return tickSize || 0.01; // Fallback to 0.01 if calculation fails
    }

    async cancelOrder(symbol, orderId) {
        // Delegate to ChaseUtils for consistent cancellation
        return this.utils?.cancelOrder(symbol, orderId);
    }

    /**
     * Cancel order with exponential backoff retry
     * Prevents orphaned single-leg positions when rollback is needed
     * @param {string} symbol - Symbol to cancel
     * @param {number} orderId - Order ID to cancel
     * @param {number} maxAttempts - Maximum attempts (default 4 for 1s/2s/4s backoff)
     */
    async cancelOrderWithRetry(symbol, orderId, maxAttempts = 4) {
        let lastError = null;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const result = await this.cancelOrder(symbol, orderId);
                if (result?.success !== false) {
                    console.log(`[PAIR-EXECUTOR] ✅ Cancelled ${symbol} order ${orderId} on attempt ${attempt}`);
                    return result;
                }
                lastError = new Error(result?.error || 'Cancel returned false');
            } catch (error) {
                lastError = error;
            }

            if (attempt < maxAttempts) {
                const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // 1s, 2s, 4s (max 5s)
                console.warn(`[PAIR-EXECUTOR] Cancel attempt ${attempt} failed for ${symbol}, retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        // All attempts failed - emit alert for manual intervention
        console.error(`[PAIR-EXECUTOR] ⚠️ CRITICAL: Failed to cancel ${symbol} order ${orderId} after ${maxAttempts} attempts. Manual intervention may be required.`);
        window.dispatchEvent(new CustomEvent('pair-executor-alert', {
            detail: {
                type: 'CANCEL_FAILED',
                symbol,
                orderId,
                message: `Failed to cancel ${symbol} order after ${maxAttempts} attempts. Check for orphaned position.`,
                error: lastError?.message
            }
        }));
        return { success: false, error: lastError?.message };
    }

    /**
     * Close an orphaned position from a partial fill
     * Uses market order to exit quickly
     * Queries actual position size to avoid oversize errors
     * @param {string} symbol - Symbol to close
     * @param {string} direction - Original direction (LONG or SHORT)
     * @param {string} executionId - Execution ID for alert correlation
     */
    async closeOrphanedPosition(symbol, direction, executionId) {
        try {
            // Query actual position size (not intended size - handles partial fills)
            const posData = await this.utils?.getPositionEntry(symbol);
            const actualSize = posData?.size;

            if (!actualSize || actualSize <= 0) {
                console.warn(`[PAIR-EXECUTOR] No position found for ${symbol}, nothing to close`);
                return { success: true, message: 'No position to close' };
            }

            // To close: sell if we were long, buy if we were short
            const closeSide = direction === 'LONG' ? 'sell' : 'buy';
            console.log(`[PAIR-EXECUTOR] Closing orphaned ${symbol} position: ${closeSide} ${actualSize} (actual size)`);

            const result = await window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbol,
                orderSide: closeSide,
                size: actualSize,  // Use actual position size
                price: null,  // Market order
                orderType: 'market',
                reduceOnly: true
            });

            if (result?.success) {
                console.log(`[PAIR-EXECUTOR] ✅ Closed orphaned ${symbol} position`);
            } else {
                console.error(`[PAIR-EXECUTOR] Failed to close orphaned ${symbol}: ${result?.error || 'unknown'}`);
                // Emit alert for manual intervention
                window.dispatchEvent(new CustomEvent('pair-executor-alert', {
                    detail: {
                        type: 'ORPHAN_CLOSE_FAILED',
                        executionId,
                        symbol,
                        direction,
                        size: actualSize,
                        message: `Failed to close orphaned ${symbol} position. Manual intervention required.`,
                        error: result?.error
                    }
                }));
            }
            return result;
        } catch (error) {
            console.error(`[PAIR-EXECUTOR] Error closing orphaned ${symbol}:`, error);
            window.dispatchEvent(new CustomEvent('pair-executor-alert', {
                detail: {
                    type: 'ORPHAN_CLOSE_FAILED',
                    executionId,
                    symbol,
                    direction,
                    message: `Exception closing orphaned ${symbol} position. Manual intervention required.`,
                    error: error.message
                }
            }));
            return { success: false, error: error.message };
        }
    }

    async startChase(orderId, symbol, direction, size, price) {
        // Delegate to ChaseUtils for consistent chase initialization
        if (!this.utils) {
            console.error('[PAIR-EXECUTOR] ChaseUtils not loaded - chase NOT started');
            return;
        }
        await this.utils.startChase(orderId, symbol, direction, size, price, this.CHASE_SETTINGS);
    }

    async monitorExecution(executionId, timeoutMs = 60000) {
        const startTime = Date.now();
        const execution = this.activeExecutions.get(executionId);

        // Validate sizes to prevent division by zero
        if (!execution?.sizeA || execution.sizeA <= 0 || !execution?.sizeB || execution.sizeB <= 0) {
            console.error(`[PAIR-EXECUTOR] Invalid sizes: A=${execution?.sizeA}, B=${execution?.sizeB}`);
            if (execution) {
                execution.status = 'FAILED';
                this.activeExecutions.delete(executionId);
            }
            return execution;
        }

        console.log(`[PAIR-EXECUTOR] Monitoring: ${execution.symbolA} + ${execution.symbolB}`);

        while (Date.now() - startTime < timeoutMs) {
            // Check if chases are still active for each leg
            // ChaseTracker's delta monitoring (500ms) handles fill detection
            const chaseA = window.BrowserChaseTracker?.getChaseForOrder({
                coin: execution.symbolA,
                side: execution.directionA === 'LONG' ? 'B' : 'A',
                sz: execution.sizeA
            });
            const chaseB = window.BrowserChaseTracker?.getChaseForOrder({
                coin: execution.symbolB,
                side: execution.directionB === 'LONG' ? 'B' : 'A',
                sz: execution.sizeB
            });

            // Check Leg A fill
            if (!chaseA && execution.legAFilled === 0) {
                // Chase stopped - verify position exists
                const positionExistsA = await this.utils?.verifyPositionExists(execution.symbolA, execution.directionA);
                if (positionExistsA) {
                    execution.legAFilled = execution.sizeA;
                    console.log(`[PAIR-EXECUTOR] Leg A filled: ${execution.symbolA}`);
                    if (window.showNotification) {
                        window.showNotification(`✅ Chase filled: ${execution.directionA} ${execution.symbolA}`, 'success');
                    }
                }
            }

            // Check Leg B fill
            if (!chaseB && execution.legBFilled === 0) {
                // Chase stopped - verify position exists
                const positionExistsB = await this.utils?.verifyPositionExists(execution.symbolB, execution.directionB);
                if (positionExistsB) {
                    execution.legBFilled = execution.sizeB;
                    console.log(`[PAIR-EXECUTOR] Leg B filled: ${execution.symbolB}`);
                    if (window.showNotification) {
                        window.showNotification(`✅ Chase filled: ${execution.directionB} ${execution.symbolB}`, 'success');
                    }
                }
            }

            // Check if both legs are filled
            if (execution.legAFilled >= execution.sizeA * 0.99 &&
                execution.legBFilled >= execution.sizeB * 0.99) {
                execution.status = 'COMPLETED';
                console.log(`[PAIR-EXECUTOR] Both legs filled - COMPLETED`);
                break;
            }

            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        if (execution.status !== 'COMPLETED') {
            execution.status = 'PARTIAL';

            // Query ACTUAL position sizes (not intended sizes) - handles partial fills correctly
            const [posDataA, posDataB] = await Promise.all([
                this.utils?.getPositionEntry(execution.symbolA),
                this.utils?.getPositionEntry(execution.symbolB)
            ]);
            const actualSizeA = posDataA?.size || 0;
            const actualSizeB = posDataB?.size || 0;
            const legAFilled = actualSizeA > 0;
            const legBFilled = actualSizeB > 0;

            console.warn(`[PAIR-EXECUTOR] Timeout - ${execution.symbolA} actual: ${actualSizeA}, ${execution.symbolB} actual: ${actualSizeB}`);

            // PARTIAL FILL CLEANUP: Handle the inconsistent state
            if (legAFilled && !legBFilled) {
                // Only leg A filled - cancel leg B order and close leg A position
                console.warn(`[PAIR-EXECUTOR] Cleaning up partial: closing ${execution.symbolA} position, cancelling ${execution.symbolB} order`);
                if (execution.currentOidB) {
                    await this.cancelOrderWithRetry(execution.symbolB, execution.currentOidB);
                }
                // Close the orphaned leg A position via market order (uses actual position size)
                await this.closeOrphanedPosition(execution.symbolA, execution.directionA, executionId);
            } else if (!legAFilled && legBFilled) {
                // Only leg B filled - cancel leg A order and close leg B position
                console.warn(`[PAIR-EXECUTOR] Cleaning up partial: cancelling ${execution.symbolA} order, closing ${execution.symbolB} position`);
                if (execution.currentOidA) {
                    await this.cancelOrderWithRetry(execution.symbolA, execution.currentOidA);
                }
                // Close the orphaned leg B position via market order (uses actual position size)
                await this.closeOrphanedPosition(execution.symbolB, execution.directionB, executionId);
            } else if (legAFilled && legBFilled) {
                // Both have positions - register with auto-close bot using ACTUAL sizes
                console.log(`[PAIR-EXECUTOR] Both legs have positions - registering with auto-close bot`);
                if (window.PairsAutoCloseBot) {
                    window.PairsAutoCloseBot.registerPosition({
                        executionId,
                        symbolA: execution.symbolA,
                        symbolB: execution.symbolB,
                        directionA: execution.directionA,
                        directionB: execution.directionB,
                        sizeA: actualSizeA,  // Use ACTUAL size
                        sizeB: actualSizeB,  // Use ACTUAL size
                        entryPriceA: posDataA?.entryPrice || execution.priceA,
                        entryPriceB: posDataB?.entryPrice || execution.priceB,
                        entryZScore: 0,
                        halfLife: 60,
                        isPartial: true  // Flag for special handling
                    });
                }
            }
            // else: neither filled - orders timed out, nothing to clean up

            // Emit user notification about partial fill
            window.dispatchEvent(new CustomEvent('pair-executor-alert', {
                detail: {
                    type: 'PARTIAL_FILL',
                    executionId,
                    symbolA: execution.symbolA,
                    symbolB: execution.symbolB,
                    legAFilled,
                    legBFilled,
                    actualSizeA,
                    actualSizeB,
                    message: `Pair trade timed out. ${legAFilled ? execution.symbolA + ` (${actualSizeA})` : ''} ${legBFilled ? execution.symbolB + ` (${actualSizeB})` : ''}`.trim()
                }
            }));
        }

        this.activeExecutions.delete(executionId);
        return execution;
    }

    /**
     * Place skewed SL/TP orders for pair trade
     * Uses 1.5x ATR for TP (aligns with mean reversion) and 0.75x ATR for SL
     * Places 4 orders total: TP and SL for each leg
     *
     * Uses ChaseUtils for consistent position entry fetching
     */
    async placeSkewedSlTp({ symbolA, symbolB, directionA, directionB, sizeA, sizeB, atrA, atrB, timeframe = '1h' }) {
        // ATR multiplier based on signal timeframe (same for TP and SL)
        // 1h signal: 1.5x ATR - wider for mean reversion
        // 1m signal: 0.5x ATR - tighter for scalping
        const ATR_MULT = timeframe === '1m' ? 0.5 : 1.5;

        console.log(`[PAIR-EXECUTOR] Using ${timeframe} multiplier: ${ATR_MULT}x ATR for both TP and SL`);
        console.log(`[PAIR-EXECUTOR] Waiting for positions to fill before placing SL/TP...`);

        // Wait for both positions using ChaseUtils (parallel for efficiency)
        const [posDataA, posDataB] = await Promise.all([
            this.utils?.getPositionEntry(symbolA),
            this.utils?.getPositionEntry(symbolB)
        ]);

        const entryA = posDataA?.entryPrice;
        const entryB = posDataB?.entryPrice;
        // Use ACTUAL position sizes from exchange (properly rounded) instead of passed-in sizes
        const actualSizeA = posDataA?.size;
        const actualSizeB = posDataB?.size;

        if (!entryA || !entryB || !actualSizeA || !actualSizeB) {
            console.error(`[PAIR-EXECUTOR] Cannot place SL/TP - positions not found (A=${entryA}/${actualSizeA}, B=${entryB}/${actualSizeB})`);
            return [];
        }

        console.log(`[PAIR-EXECUTOR] ✅ Both positions found: ${symbolA}=${entryA} (size=${actualSizeA}), ${symbolB}=${entryB} (size=${actualSizeB})`);

        // Calculate TP/SL prices using actual entry prices
        const tpPriceA = directionA === 'LONG' ? entryA + (atrA * ATR_MULT) : entryA - (atrA * ATR_MULT);
        const slPriceA = directionA === 'LONG' ? entryA - (atrA * ATR_MULT) : entryA + (atrA * ATR_MULT);
        const tpPriceB = directionB === 'LONG' ? entryB + (atrB * ATR_MULT) : entryB - (atrB * ATR_MULT);
        const slPriceB = directionB === 'LONG' ? entryB - (atrB * ATR_MULT) : entryB + (atrB * ATR_MULT);

        console.log(`[PAIR-EXECUTOR] Placing skewed SL/TP orders:`, {
            legA: { symbol: symbolA, entry: entryA, tp: tpPriceA.toFixed(4), sl: slPriceA.toFixed(4) },
            legB: { symbol: symbolB, entry: entryB, tp: tpPriceB.toFixed(4), sl: slPriceB.toFixed(4) }
        });

        // Place TP/SL using tpslEnabled pattern (like chase-tracker)
        // orderSide = entry side (SDK flips internally for reduce-only)
        // Use actualSizeA/B from exchange (properly rounded) to avoid "invalid size" errors
        const orders = await Promise.allSettled([
            // Leg A TP
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbolA,
                orderSide: directionA === 'LONG' ? 'buy' : 'sell',
                size: actualSizeA,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: tpPriceA,
                slPrice: null
            }),
            // Leg A SL
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbolA,
                orderSide: directionA === 'LONG' ? 'buy' : 'sell',
                size: actualSizeA,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: null,
                slPrice: slPriceA
            }),
            // Leg B TP
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbolB,
                orderSide: directionB === 'LONG' ? 'buy' : 'sell',
                size: actualSizeB,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: tpPriceB,
                slPrice: null
            }),
            // Leg B SL
            window.BrowserHyperliquidClient?.executeTradingOrder({
                asset: symbolB,
                orderSide: directionB === 'LONG' ? 'buy' : 'sell',
                size: actualSizeB,
                price: null,
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,
                tpPrice: null,
                slPrice: slPriceB
            })
        ]);

        // Log results
        const results = orders.map((o, i) => ({
            order: ['A_TP', 'A_SL', 'B_TP', 'B_SL'][i],
            status: o.status,
            value: o.value?.success,
            error: o.reason?.message || o.value?.error
        }));
        console.log(`[PAIR-EXECUTOR] SL/TP order results:`, results);

        return results;
    }

    async cancelExecution(executionId) {
        const execution = this.activeExecutions.get(executionId);
        if (execution) {
            execution.status = 'CANCELLED';

            // Cancel pending orders and wait for completion
            const promises = [];
            if (execution.currentOidA) {
                promises.push(this.cancelOrder(execution.symbolA, execution.currentOidA).catch(e => {
                    console.warn(`[PAIR-EXECUTOR] Failed to cancel order A: ${e.message}`);
                }));
            }
            if (execution.currentOidB) {
                promises.push(this.cancelOrder(execution.symbolB, execution.currentOidB).catch(e => {
                    console.warn(`[PAIR-EXECUTOR] Failed to cancel order B: ${e.message}`);
                }));
            }
            await Promise.allSettled(promises);

            this.activeExecutions.delete(executionId);
        }
    }

    getActiveExecutions() {
        return Array.from(this.activeExecutions.entries()).map(([id, exec]) => ({
            executionId: id,
            ...exec
        }));
    }
}

// Export to window for V4 browser services
if (typeof window !== 'undefined') {
    window.PairTradeExecutor = new PairTradeExecutor();
}
